import { Buffer } from 'buffer';

import React, { useRef, useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { ResponseError } from '../../../common/spdCore/autogenerated/spdApiClient';
import FormRow from '../../../app/components/FormRow';

interface UserCertificateUploadProps {
  userId: string;
  hasCertificate?: boolean;
  currentPwz?: string;
  currentOccupationalMedicineDoctorIdentifier?: string;
  onSuccess?: () => void;
}

export const UserCertificateUpload: React.FC<
  UserCertificateUploadProps
> = props => {
  const spd = useSpdCore();
  const notifications = useNotifications();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [p12Base64, setP12Base64] = useState('');
  const [fileName, setFileName] = useState('');
  const [password, setPassword] = useState('');
  const [pwz, setPwz] = useState(props.currentPwz ?? '');
  const [
    occupationalMedicineDoctorIdentifier,
    setOccupationalMedicineDoctorIdentifier,
  ] = useState(props.currentOccupationalMedicineDoctorIdentifier ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [uploaded, setUploaded] = useState(false);

  const handleFileChange = (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    const f = files[0];
    setFileName(f.name);
    const reader = new FileReader();
    reader.readAsArrayBuffer(f);
    reader.onload = e => {
      if (e.target?.result) {
        setP12Base64(
          Buffer.from(e.target.result as ArrayBuffer).toString('base64'),
        );
      }
    };
  };

  const handleUpload = async () => {
    if (!p12Base64) {
      setErrors(['Wybierz plik certyfikatu (.p12 / .pfx).']);
      setShowErrors(true);
      return;
    }
    if (!password) {
      setErrors(['Podaj hasło do certyfikatu.']);
      setShowErrors(true);
      return;
    }

    try {
      setIsSaving(true);
      await spd.users.uploadCertificate(props.userId, {
        p12Base64,
        password,
        pwz: pwz || undefined,
        occupationalMedicineDoctorIdentifier:
          occupationalMedicineDoctorIdentifier || undefined,
      });
      setShowErrors(false);
      setUploaded(true);
      setP12Base64('');
      setFileName('');
      setPassword('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      notifications.onPromise(Promise.resolve());
      props.onSuccess?.();
    } catch (response) {
      const errs = await responseErrors(response as ResponseError);
      setErrors(errs);
      setShowErrors(true);
    } finally {
      setIsSaving(false);
    }
  };

  const hasCert = uploaded || props.hasCertificate;

  return (
    <>
      {hasCert && (
        <div className="mb-2">
          <Badge bg="success">Certyfikat załadowany</Badge>
        </div>
      )}
      <Form className="d-grid gap-2">
        <FormRow controlId="cert_file" label="Plik .p12 / .pfx">
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => fileInputRef.current?.click()}
            >
              Wybierz plik
            </Button>
            {fileName && <span className="text-muted small">{fileName}</span>}
            <Form.Control
              type="file"
              accept=".p12,.pfx"
              onChange={e =>
                handleFileChange((e.currentTarget as HTMLInputElement).files)
              }
              style={{ display: 'none' }}
              ref={fileInputRef}
            />
          </div>
        </FormRow>
        <FormRow controlId="cert_password" label="Hasło">
          <Form.Control
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Hasło do certyfikatu"
          />
        </FormRow>
        <FormRow controlId="cert_pwz" label="Numer PWZ">
          <Form.Control
            type="text"
            value={pwz}
            onChange={e => setPwz(e.target.value)}
            placeholder="Numer prawa wykonywania zawodu"
          />
        </FormRow>
        <FormRow
          controlId="cert_om_doctor_id"
          label="Identyfikator lekarza medycyny pracy"
        >
          <Form.Control
            type="text"
            value={occupationalMedicineDoctorIdentifier}
            onChange={e =>
              setOccupationalMedicineDoctorIdentifier(e.target.value)
            }
          />
        </FormRow>
        <div className="d-flex justify-content-end">
          <Button
            variant="outline-primary"
            onClick={handleUpload}
            disabled={isSaving}
          >
            {isSaving ? 'Wgrywanie…' : 'Wgraj certyfikat'}
          </Button>
        </div>
      </Form>
      <ValidationAlert show={showErrors} errors={errors} className="mt-2" />
    </>
  );
};
